import { useMutation } from "@tanstack/vue-query";
import { type Ref, readonly, ref } from "vue";

import URLS from "@/autogen/urls";

type MagicLinkStatus = "idle" | "pending" | "success" | "error";

export function useMagicLink() {
  const status = ref<MagicLinkStatus>("idle");
  const message = ref("");
  const captchaRequired = ref(false);
  const captchaToken = ref("");

  const sendMutation = useMutation({
    async mutationFn(params: { email: string; captchaToken?: string }) {
      const formData = new FormData();
      formData.append("email", params.email);
      formData.append("return_url", window.location.href);
      if (params.captchaToken) {
        formData.append("cf-turnstile-response", params.captchaToken);
      }

      const response = await fetch(URLS["portal"](), {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === "captcha_required") {
          return { captchaRequired: true } as const;
        }
        throw new Error(data.error || "Failed to send sign-in link");
      }

      return { success: true } as const;
    },
    onSuccess(result) {
      if ("captchaRequired" in result && result.captchaRequired) {
        captchaRequired.value = true;
        status.value = "idle";
        return;
      }

      status.value = "success";
      message.value = "";
      captchaRequired.value = false;
      captchaToken.value = "";
    },
    onError(error: Error) {
      if (captchaToken.value) {
        captchaRequired.value = false;
        captchaToken.value = "";
      }
      status.value = "error";
      message.value = error.message;
    },
  });

  const send = (email: string) => {
    if (!email) return;
    if (status.value === "pending") return;
    status.value = "pending";
    sendMutation.mutate({
      email,
      captchaToken: captchaToken.value || undefined,
    });
  };

  const setCaptchaToken = (token: string) => {
    captchaToken.value = token;
  };

  const handleCaptchaError = (errorMessage: string) => {
    message.value = errorMessage;
    captchaRequired.value = false;
    captchaToken.value = "";
  };

  const reset = () => {
    status.value = "idle";
    message.value = "";
    captchaRequired.value = false;
    captchaToken.value = "";
  };

  return {
    status: readonly(status) as Ref<MagicLinkStatus>,
    message: readonly(message) as Ref<string>,
    captchaRequired: readonly(captchaRequired) as Ref<boolean>,
    send,
    setCaptchaToken,
    handleCaptchaError,
    reset,
  };
}
